import {
    normalizeAdvisorDisplayName,
    normalizeOpportunityAccountId,
} from "src/services/api/opportunities/accountNormalize"
import type {
    IOpportunity,
    MovementPortfolioRollup,
    OpportunityImpactedClient,
} from "src/services/api/opportunities/types"
import { getObjectiveListKey } from "./objectiveListKey"

/* ------------------------------------------------------------------ */
/* Tipos                                                               */
/* ------------------------------------------------------------------ */

/** Carteira (cliente impactado) com participação no recorte e execução. */
export interface CarteiraComposition {
    account: string
    /** "Nome (conta)" para exibição. */
    label: string
    advisorKey: string
    advisorLabel: string
    /** Valor da oportunidade (potencial) desta carteira. */
    value: number
    /** Valor executado (líquido) casado por conta. */
    executedValue: number
    /** executedValue / value (null quando value <= 0). */
    executionRatio: number | null
}

/** Agrupamento por assessor dentro de uma oportunidade. */
export interface AdvisorComposition {
    advisorKey: string
    advisorLabel: string
    /** Soma do potencial das carteiras deste assessor. */
    value: number
    /** Participação do assessor no valor da oportunidade (0–1). */
    share: number
    executedValue: number
    executionRatio: number | null
    /** Clientes com % de volume > 0. */
    clientsDone: number
    clientsTotal: number
    carteiras: CarteiraComposition[]
}

/** Composição de uma oportunidade: potencial + execução, segmentada por assessor. */
export interface OpportunityComposition {
    key: string
    title: string
    /** Soma do volume de oportunidade (potencial). */
    opportunityValue: number
    /** Soma do líquido executado casado por conta. */
    executedValue: number
    /** executedValue / opportunityValue (null quando potencial <= 0). */
    executionRatio: number | null
    /** Assessores ordenados por participação desc. */
    advisors: AdvisorComposition[]
    /** Todas as carteiras ordenadas por valor desc. */
    carteiras: CarteiraComposition[]
}

/** Participação de um assessor em UMA oportunidade (fatia da barra agregada). */
export interface AdvisorOpportunitySlice {
    oppKey: string
    oppTitle: string
    /** Potencial do assessor nesta oportunidade. */
    potential: number
    /** Executado (líquido) do assessor nesta oportunidade. */
    executed: number
    /** executed / potential (null quando potential <= 0). */
    ratio: number | null
    clientsDone: number
    clientsTotal: number
    carteiras: CarteiraComposition[]
}

/** Assessor agregado entre as oportunidades selecionadas. */
export interface AdvisorAggregate {
    advisorKey: string
    advisorLabel: string
    totalPotential: number
    totalExecuted: number
    /** Média ponderada pelo valor: totalExecuted / totalPotential. */
    aggregateRatio: number | null
    /** Contas únicas com execução > 0% em pelo menos uma oportunidade. */
    clientsDone: number
    clientsTotal: number
    /** Fatias por oportunidade, ordenadas por potencial desc. */
    slices: AdvisorOpportunitySlice[]
}

export interface OpportunityLegendItem {
    key: string
    title: string
    color: string
}

export interface AdvisorAggregatedModel {
    advisors: AdvisorAggregate[]
    /** Oportunidades selecionadas, na ordem da listagem, com cor estável. */
    opportunities: OpportunityLegendItem[]
    /** oppKey → cor. */
    opportunityColors: Map<string, string>
}

/* ------------------------------------------------------------------ */
/* Paleta de cores dos segmentos (assessores)                          */
/* ------------------------------------------------------------------ */

/** Cores alinhadas à marca, legíveis sobre fundo escuro. */
const SEGMENT_COLORS = [
    "#A2CDED", // primary.main
    "#BD6DB6", // tertiary.light
    "#79A7FF", // accentInfo
    "#79FF8B", // accentSuccess
    "#80AFD1", // secondary.main
    "#6D9ABC", // primary.dark
    "#853C7F", // tertiary.main
    "#FF7979", // accentError
    "#4B88AD", // primary.darker
    "#DAEAF7", // secondary.lighter
]

const UNASSIGNED_ADVISOR_KEY = "__unassigned__"
/** Cor neutra para "Sem assessor". */
export const UNASSIGNED_ADVISOR_COLOR = "#686661" // neutral.dark

const advisorKeyFor = (client: OpportunityImpactedClient): string => {
    const id = client.advisor_id != null ? String(client.advisor_id).trim() : ""
    if (id) return `id:${id}`
    const name = normalizeAdvisorDisplayName(client.advisor_name)
    if (name) return `name:${name.toLocaleUpperCase("pt-BR")}`
    return UNASSIGNED_ADVISOR_KEY
}

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

const finiteOrZero = (n: unknown): number => {
    const v = Number(n)
    return Number.isFinite(v) ? v : 0
}

/** account normalizada → líquido executado (soma das linhas de movimento). */
const executedByAccount = (rollups: MovementPortfolioRollup[] | undefined): Map<string, number> => {
    const m = new Map<string, number>()
    for (const r of rollups ?? []) {
        const key = normalizeOpportunityAccountId(r.portfolio_account ?? r.account)
        if (!key) continue
        m.set(key, (m.get(key) ?? 0) + finiteOrZero(r.total_amount_net))
    }
    return m
}

const ratio = (executed: number, potential: number): number | null =>
    potential > 0 ? executed / potential : null

/** Cliente feito = qualquer execução de volume financeiro acima de 0%. */
const isClientExecuted = (executionRatio: number | null): boolean =>
    executionRatio != null && Number.isFinite(executionRatio) && executionRatio > 0

const countClients = (
    carteiras: Array<{ account: string; executionRatio: number | null }>,
    uniqueByAccount = false,
): { clientsDone: number; clientsTotal: number } => {
    if (!uniqueByAccount) {
        return {
            clientsDone: carteiras.filter((c) => isClientExecuted(c.executionRatio)).length,
            clientsTotal: carteiras.length,
        }
    }
    const byAccount = new Map<string, boolean>()
    for (const c of carteiras) {
        const key = c.account.trim() || `anon:${byAccount.size}`
        byAccount.set(key, (byAccount.get(key) ?? false) || isClientExecuted(c.executionRatio))
    }
    let clientsDone = 0
    for (const done of Array.from(byAccount.values())) {
        if (done) clientsDone += 1
    }
    return { clientsDone, clientsTotal: byAccount.size }
}

const carteiraLabel = (client: OpportunityImpactedClient, account: string): string => {
    const name = String(client.name ?? client.p?.name ?? "").trim()
    return name && account ? `${name} (${account})` : name || account || "—"
}

/* ------------------------------------------------------------------ */
/* Composição de uma oportunidade                                      */
/* ------------------------------------------------------------------ */

export const buildOpportunityComposition = (objective: IOpportunity): OpportunityComposition => {
    const executed = executedByAccount(objective.movement_portfolio_rollups)
    const advisorMap = new Map<string, AdvisorComposition>()
    const carteiras: CarteiraComposition[] = []

    let opportunityValue = 0
    let executedValue = 0

    for (const client of objective.impacted_clients ?? []) {
        const account = normalizeOpportunityAccountId(client.account ?? client.p?.account)
        const value = finiteOrZero(client.volumeOportunidade)
        const execVal = account ? finiteOrZero(executed.get(account)) : 0
        const advisorKey = advisorKeyFor(client)
        const advisorLabel = normalizeAdvisorDisplayName(client.advisor_name) ?? ""

        const carteira: CarteiraComposition = {
            account,
            label: carteiraLabel(client, account),
            advisorKey,
            advisorLabel,
            value,
            executedValue: execVal,
            executionRatio: ratio(execVal, value),
        }
        carteiras.push(carteira)

        opportunityValue += value
        executedValue += execVal

        const prev = advisorMap.get(advisorKey)
        if (prev) {
            prev.value += value
            prev.executedValue += execVal
            prev.advisorLabel = prev.advisorLabel || advisorLabel
            prev.carteiras.push(carteira)
        } else {
            advisorMap.set(advisorKey, {
                advisorKey,
                advisorLabel,
                value,
                share: 0,
                executedValue: execVal,
                executionRatio: null,
                clientsDone: 0,
                clientsTotal: 0,
                carteiras: [carteira],
            })
        }
    }

    const advisors = Array.from(advisorMap.values())
        .map((a) => {
            const { clientsDone, clientsTotal } = countClients(a.carteiras)
            return {
                ...a,
                share: opportunityValue > 0 ? a.value / opportunityValue : 0,
                executionRatio: ratio(a.executedValue, a.value),
                clientsDone,
                clientsTotal,
                carteiras: [...a.carteiras].sort((x, y) => y.value - x.value),
            }
        })
        .sort((x, y) => y.value - x.value)

    return {
        key: getObjectiveListKey(objective),
        title: objective.title,
        opportunityValue,
        executedValue,
        executionRatio: ratio(executedValue, opportunityValue),
        advisors,
        carteiras: [...carteiras].sort((x, y) => y.value - x.value),
    }
}

/* ------------------------------------------------------------------ */
/* Modelo agregado por assessor (várias oportunidades)                 */
/* ------------------------------------------------------------------ */

export const buildAdvisorAggregatedModel = (
    objectives: IOpportunity[]
): AdvisorAggregatedModel => {
    const comps = objectives.map(buildOpportunityComposition)

    // Cor por oportunidade, estável na ordem da listagem.
    const opportunities: OpportunityLegendItem[] = comps.map((c, idx) => ({
        key: c.key,
        title: c.title,
        color: SEGMENT_COLORS[idx % SEGMENT_COLORS.length],
    }))
    const opportunityColors = new Map(opportunities.map((o) => [o.key, o.color]))

    // Pivot: agrupa as participações de cada assessor por oportunidade.
    const advisorMap = new Map<string, AdvisorAggregate>()
    for (const c of comps) {
        for (const a of c.advisors) {
            const slice: AdvisorOpportunitySlice = {
                oppKey: c.key,
                oppTitle: c.title,
                potential: a.value,
                executed: a.executedValue,
                ratio: a.executionRatio,
                clientsDone: a.clientsDone,
                clientsTotal: a.clientsTotal,
                carteiras: a.carteiras,
            }
            const prev = advisorMap.get(a.advisorKey)
            if (prev) {
                prev.totalPotential += a.value
                prev.totalExecuted += a.executedValue
                prev.advisorLabel = prev.advisorLabel || a.advisorLabel
                prev.slices.push(slice)
            } else {
                advisorMap.set(a.advisorKey, {
                    advisorKey: a.advisorKey,
                    advisorLabel: a.advisorLabel,
                    totalPotential: a.value,
                    totalExecuted: a.executedValue,
                    aggregateRatio: null,
                    clientsDone: 0,
                    clientsTotal: 0,
                    slices: [slice],
                })
            }
        }
    }

    const advisors = Array.from(advisorMap.values()).map((a) => {
        const allCarteiras = a.slices.flatMap((s) => s.carteiras)
        const { clientsDone, clientsTotal } = countClients(allCarteiras, true)
        return {
            ...a,
            clientsDone,
            clientsTotal,
            aggregateRatio: ratio(a.totalExecuted, a.totalPotential),
            slices: [...a.slices].sort((x, y) => y.potential - x.potential),
        }
    })

    return { advisors, opportunities, opportunityColors }
}

export { UNASSIGNED_ADVISOR_KEY }
